import type React from 'react'
import { useState, useEffect } from 'react'
import { Card, Form, message, Button, Tag } from 'antd'
import { Vertical, Texto, Horizontal, GraviButton, BBDTag } from '@gravitate-js/excalibrr'
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons'
import {
  useSupplierReconciliation,
  type SupplierConfig,
  type GlobalConfig,
  type WarningConfig,
  type AutoApproveConfig,
  type AutoMatchConfig,
} from '@api/useSupplierReconciliation'
import _ from 'lodash'
import { UnifiedConfigTable } from './UnifiedConfigTable/index'

interface SupplierConfigPanelProps {
  selectedSupplier?: SupplierConfig
  defaultConfig?: GlobalConfig
}

/**
 * Unified Supplier Configuration Panel
 * Shows all config types (match, approve, warning) in a single table view
 */
export function SupplierConfigPanel({ selectedSupplier, defaultConfig }: SupplierConfigPanelProps) {
  const [form] = Form.useForm()
  const [isDirty, setIsDirty] = useState(false)
  const [formReady, setFormReady] = useState(false)

  const { useUpsertConfig } = useSupplierReconciliation()
  const { mutateAsync: updateSupplierConfig, isPending } = useUpsertConfig()

  // Set initial form values when supplier changes
  useEffect(() => {
    if (!selectedSupplier || !defaultConfig) {
      setFormReady(false)
      return
    }

    // Helper function to merge config with defaults
    const mergeWithDefaults = (supplierConfig: unknown, defaultConfig: unknown) => {
      if (supplierConfig === null || supplierConfig === undefined) {
        return defaultConfig
      }
      return supplierConfig
    }

    const initialValues = {
      auto_match_rules: mergeWithDefaults(selectedSupplier.auto_match_rules, defaultConfig.auto_match_rules),
      auto_approve_rules: mergeWithDefaults(selectedSupplier.auto_approve_rules, defaultConfig.auto_approve_rules),
      warning_rules: mergeWithDefaults(selectedSupplier.warning_rules, defaultConfig.warning_rules),
    }

    // Reset form and set new values
    form.resetFields()

    // Use setTimeout to ensure form is fully reset before setting new values
    setTimeout(() => {
      form.setFieldsValue(initialValues)
      setFormReady(true)
      setIsDirty(false)
    }, 0)
  }, [selectedSupplier?.supplier_id, defaultConfig, form])

  // Handle save
  const handleSave = async (values: SupplierConfig) => {
    if (!selectedSupplier) return

    await updateSupplierConfig({
      body: {
        supplier_id: selectedSupplier.supplier_id,
        assignee: selectedSupplier.assignee || null,
        external_names: selectedSupplier.external_name_mappings || [],
        auto_match_rules: values.auto_match_rules,
        auto_approve_rules: values.auto_approve_rules,
        warning_rules: values.warning_rules,
      },
    })

    message.success(`Configuration updated for ${selectedSupplier.supplier_name}`)
    setIsDirty(false)
  }

  // Handle reset to global
  const handleResetToGlobal = async () => {
    if (!selectedSupplier || !defaultConfig) return

    await updateSupplierConfig({
      body: {
        supplier_id: selectedSupplier.supplier_id,
        assignee: selectedSupplier.assignee || null,
        external_names: selectedSupplier.external_name_mappings || [],
        auto_match_rules: null,
        auto_approve_rules: null,
        warning_rules: null,
      },
    })

    message.success(`Configuration reset to global defaults for ${selectedSupplier.supplier_name}`)

    setIsDirty(false)
  }

  // Check if supplier has custom config (any of the three config types)
  const hasCustomConfig =
    selectedSupplier?.auto_match_rules !== null ||
    selectedSupplier?.auto_approve_rules !== null ||
    selectedSupplier?.warning_rules !== null

  if (!selectedSupplier || !defaultConfig || !formReady) return null

  return (
    <Card
      styles={{
        title: {
          padding: 0,
        },
        body: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
        },
      }}
      style={{
        padding: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Horizontal
        justifyContent='space-between'
        alignItems='center'
        className='px-3 py-4'
        style={{ width: '100%' }}
        bg={hasCustomConfig ? 'warning' : 'elevated'}
        border='all'
      >
        <Texto category='h5' weight='bold'>
          {selectedSupplier.supplier_name}
        </Texto>
        {hasCustomConfig && <BBDTag>This config has been overridden</BBDTag>}
      </Horizontal>
      <Form
        key={selectedSupplier.supplier_id}
        onFinishFailed={(errors) => console.log(errors)}
        form={form}
        name='supplierConfig'
        onFinish={handleSave}
        layout='vertical'
        style={{ overflow: 'auto', height: '100%' }}
      >
        <div style={{ flex: 1 }}>
          <UnifiedConfigTable form={form} onFormChange={() => setIsDirty(true)} />
        </div>
      </Form>
      <Horizontal
        style={{ flex: 0, overflow: 'visible' }}
        justifyContent='space-between'
        alignItems='center'
        border='top'
        bg='default'
        className='p-3'
      >
        {hasCustomConfig && (
          <GraviButton
            buttonText='Reset to Global'
            icon={<ReloadOutlined />}
            theme2
            appearance='text'
            onClick={handleResetToGlobal}
            disabled={isPending}
          />
        )}
        <Horizontal gap={8} style={{ marginLeft: 'auto' }}>
          <GraviButton
            appearance='text'
            onClick={() => {
              // Reset form to original values
              form.resetFields()
              // Re-apply the merged values
              const mergeWithDefaults = (supplierConfig: unknown, defaultConfig: unknown) => {
                if (supplierConfig === null || supplierConfig === undefined) {
                  return defaultConfig
                }
                return supplierConfig
              }

              const initialValues = {
                auto_match_rules: mergeWithDefaults(selectedSupplier.auto_match_rules, defaultConfig.auto_match_rules),
                auto_approve_rules: mergeWithDefaults(
                  selectedSupplier.auto_approve_rules,
                  defaultConfig.auto_approve_rules
                ),
                warning_rules: mergeWithDefaults(selectedSupplier.warning_rules, defaultConfig.warning_rules),
              }

              form.setFieldsValue(initialValues)
              setIsDirty(false)
            }}
          >
            Cancel
          </GraviButton>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            loading={isPending}
            disabled={!isDirty}
            onClick={() => form.submit()}
          >
            Save Changes
          </Button>
        </Horizontal>
      </Horizontal>
    </Card>
  )
}
